import courseService from 'ApiService/Admin/courseService';
import teacherService from 'ApiService/Admin/teacherService';
import Card from 'components/card';
import { AuthContext } from 'contexts/AuthContext';
import ConfirmationModal from 'pages/Common/ConfirmationModal';
import { useCallback, useContext, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import Stepper from 'views/admin/main/users/newUser/components/Stepper';
import StepperControl from 'views/admin/main/users/newUser/components/Subject/StepperControl';
import { UseContextProvider } from 'views/admin/main/users/newUser/contexts/StepperContext';
import SelectBatches from './Steps/SelectBatches';
import SelectTeacher from './Steps/SelectTeacher';
import SelectDepartment from './Steps/SelectDepartment';

const Index = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [testData, setTestData] = useState({
    courseId: '',
    batchStructureId: '',
    batchId: '',
  });
  const [disable, setDisable] = useState(true);
  const [loading, setLoading] = useState(false);
  const [courses, setCourses] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { state } = useContext(AuthContext);

  const steps = [
    { stepNo: 1, name: 'Select Department' },
    { stepNo: 2, name: 'Course Info' },
    { stepNo: 3, name: 'Assign Teacher' },
  ];

  const fetchCourses = useCallback(async () => {
    if (!selectedDepartment) return;
    setLoadingCourses(true);
    setCourses([]);
    try {
      const response = await courseService.getCourseBatchInfo({
        departmentId: selectedDepartment,
        selectedBranch: state?.selectedBranch, // Still pass branch for context if available
      });
      if (response.data.success) {
        setCourses(response.data.courses);
      } else {
        toast.error('Failed to fetch courses.');
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to fetch courses.');
    } finally {
      setLoadingCourses(false);
    }
  }, [selectedDepartment, state?.selectedBranch]);
  const handleAssignTeacher = async () => {
    if (!selectedTeacher || !testData.batchId) {
      toast.error('Please select a teacher and a batch.');
      return;
    }

    setIsModalOpen(true);
  };

  const confirmAssignment = async () => {
    if (loading) return;
    setLoading(true);
    const payload = {
      batchId: testData.batchId,
      teacherId: selectedTeacher,
      courseId: testData.courseId,
      batchStructureId: testData.batchStructureId,
      selectedBranch: state?.selectedBranch,
    };
    try {
      const response = await teacherService.assignTeacherToBatches(payload);
      if (response.data?.success) {
        toast.success(response?.data?.message);
        setSelectedTeacher(null);
        setTestData({
          courseId: '',
          batchStructureId: '',
          batchId: '',
        });
        setCurrentStep(2);
      } else {
        toast.error(response.data.message || 'Failed to assign assignment.');
      }
    } catch (error) {
      toast.error(error?.response?.data?.message);
    } finally {
      setLoading(false);

      setIsModalOpen(false);
    }
  };

  useEffect(() => {
    if (selectedDepartment) {
      fetchCourses();
    }
  }, [selectedDepartment, fetchCourses]);

  useEffect(() => {
    if (!selectedDepartment) {
      setCourses([]);
      setTestData({
        courseId: '',
        batchStructureId: '',
        batchId: '',
      });
      setSelectedTeacher(null);
    }
  }, [selectedDepartment]);

  const resetAll = () => {
    setSelectedDepartment('');
    setCourses([]);
    setTestData({
      courseId: '',
      batchStructureId: '',
      batchId: '',
    });
    setSelectedTeacher(null);
  };

  const displayStep = (step) => {
    switch (step.stepNo) {
      case 1:
        return (
          <SelectDepartment
            setDisable={setDisable}
            selectedDepartment={selectedDepartment}
            setSelectedDepartment={setSelectedDepartment}
          />
        );
      case 2:
        return (
          <SelectBatches
            setLoading={setLoading}
            setDisable={setDisable}
            courses={courses}
            setTestData={setTestData}
            testData={testData}
            loadingCourses={loadingCourses}
          />
        );
      case 3:
        return (
          <SelectTeacher
            setLoading={setLoading}
            setDisable={setDisable}
            testData={testData}
            selectedTeacher={selectedTeacher}
            setSelectedTeacher={setSelectedTeacher}
            selectedDepartment={selectedDepartment}
          />
        );
      default:
        return null;
    }
  };

  const handleClick = (direction) => {
    let newStep = currentStep;
    direction === 'next' ? newStep++ : newStep--;
    if (direction === 'prev' && newStep === 1) {
      resetAll();
    }
    newStep > 0 && newStep <= steps.length && setCurrentStep(newStep);
  };

  return (
    <div className="md-mt-[3] mt-3 h-full w-full">
      <div className="h-[350px] w-full rounded-[20px] bg-gradient-to-br from-brand-400 to-brand-600 md:h-[390px]" />
      <div className="w-md:2/3 mx-auto h-full w-5/6 md:px-3  3xl:w-7/12">
        <div className="-mt-[280px] w-full pb-10 md:-mt-[340px] md:px-[70px]">
          <Stepper steps={steps} currentStep={currentStep} />
        </div>
        <Card extra={'h-full mx-auto pb-3'}>
          <div className="rounded-[20px]">
            <UseContextProvider>
              {displayStep(steps[currentStep - 1])}
            </UseContextProvider>
          </div>
          {/* navigation button */}
          <StepperControl
            handleClick={handleClick}
            currentStep={currentStep}
            steps={steps}
            loading={loading}
            disable={disable}
            handleSubmit={handleAssignTeacher}
          />
        </Card>
      </div>
      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={confirmAssignment}
        title="Confirm Teacher Assignment"
        message={`Are you sure you want to assign the selected teacher to this batch?`}
        confirmText="Assign Teacher"
        cancelText="Cancel"
        icon="info"
        disableConfirm={loading}
      />
    </div>
  );
};

export default Index;
